/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {
  useCallback,
  useMemo
} from 'react';
import {
  useQuery,
  useQueryClient
} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {getDeviceShippingInfoByPuls} from '@ion/core/src/services/Connect';
import {GenericObject} from '@ion/core/src/types/entities';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, DurationResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseDeviceShippingInfoResult = UseDefaultQueryResult & {
  shippingInfo: GenericObject;
  refresh: () => void;
};

/**
 * Retrieves shipping information of the device with the given identifier.
 * @param deviceIdentifier the identifier of the device (either the esn or serial number).
 */
const getDeviceShippingInfo = async(deviceIdentifier: string): Promise<GenericObject | null> => {
  try {
    const shippingInfo = await getDeviceShippingInfoByPuls(deviceIdentifier);
    return shippingInfo;
  } catch (err) {
    if (err?.is404) {
      return null;
    } else {
      error(err);
    }
  }
};

/**
 * Retrieves the shipping information for the device of the given identifier.
 *
 * @param deviceIdentifier identifier of the device to retrieve information for (esn or serial number)
 * @param enabled if the query is enabled / disabled
 * @returns a query result
 */
export const useDeviceShippingInfo = (deviceIdentifier: string, enabled = true): UseDeviceShippingInfoResult => {
  const queryClient = useQueryClient();
  const key         = useMemo(() => (
    createKey(QueryKey.DEVICE_SHIPPING_INFO, deviceIdentifier)
  ), [deviceIdentifier]);
  const query       = useQuery(key, async() => {
    const results = await getDeviceShippingInfo(deviceIdentifier);
    return results;
  }, {
    ...getStaleTime(5) as DurationResult,
    enabled
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries(key);
  }, [key]);

  return createQueryResult(query, {
    shippingInfo : query.data || {},
    refresh
  }) as UseDeviceShippingInfoResult;
};

export default useDeviceShippingInfo;
